<template>
  <div>
    <sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      @refetch-data="refetchData"
      :tenant-options="tenantOptions"
      :unit-options="unitOptions"
    />

    <b-row class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <filters
          :tenant-filter.sync="tenantFilter"
          :tenant-options="tenantOptions"
          :unit-filter.sync="unitFilter"
          :unit-options="unitOptions"
          :alarm-profile-filter.sync="alarmProfileFilter"
          :alarm-profile-options="alarmProfileOptions"
          :region-options="regionOptions"
          :municipality-options="municipalityOptions"
          :custom-query-params.sync="customQueryParams"
        />
      </template>
    </filter-modal>
    <!-- Table Container Card -->
    <b-card class="mx-2 my-1" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="4"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="text-capitalize">{{ $t("table.show") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label class="text-capitalize">{{ $t("table.entries") }}</label>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="8">
            <div class="device-list-actions d-flex align-items-center justify-content-end">
              <FacetedSearch
                :options="searchOptions"
                default-field="name"
                class="device-list-search mr-1"
                @search="setFacetedFilters"
              />
              <b-button
                variant="primary"
                @click="openAdd()"
                v-if="$can('create', 'HanwhaDvr')"
              >
                <span class="text-nowrap text-capitalize">{{
                  `${$t("actions.add")} ${$tc("hanwha_dvr.title", 1)}`
                }}</span>
              </b-button>
              <b-button
                variant="success"
                class="text-capitalize ml-1"
                type="button"
                :disabled="buttonExport"
                :title="$t('messages.export.title')"
                @click="exportExcel"
              >
                <feather-icon icon="DownloadCloudIcon" size="15" />
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_record_found')"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <template #head(name)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(description)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(tenant)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(recording_days)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(days_recording_audit)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(created_at)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(updated_at)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(active)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #head(actions)="data">
          <span> {{ $tc(data.label, 2) }}</span>
        </template>

        <template #cell(networks)="data">
          <span >
            {{ data.value.map(item => item.mac_address).join(', ') }}
          </span>
        </template>

        <template #cell(enabled_recording_audit)="data">
          <div class="text-center">
            <feather-icon
            :icon="data.value ? 'CheckCircleIcon' : 'XCircleIcon'"
            :class="data.value ? 'text-success' : 'text-danger'"
            size="18"
            />
          </div>
        </template>

        <template #cell(name)="data">
          <b-media vertical-align="center">
            <template #aside>
              <div
                :style="`background: ${data.item.online ? 'green' : 'red'};
                        width: 1.5rem;
                        height: 1.5rem;
                        border-radius: 1rem;
                        text-align: center;`"
              ></div>
            </template>
            <small>{{ data.item.name }}</small>
          </b-media>
        </template>

        <template #cell(password)="data">
          <div
            v-clipboard:copy="`${data.value}`"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
            style="cursor: pointer"
          >
            <feather-icon icon="CopyIcon" size="16" class="align-middle text-body" />
          </div>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button
            :id="`actions-${data.item.id}`"
            tabindex="0"
            variant="white"
            class="px-1 btn-y"
          >
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </b-button>
          <b-popover
            :target="`actions-${data.item.id}`"
            triggers="focus"
            placement="bottom"
            custom-class="popover-p-0"
            variant="white"
          >
            <b-list-group class="m-0">
              <b-list-group-item
                v-if="$can('update', 'HanwhaDvr')"
                class="border-0"
                button
                @click="openEdit(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.edit") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('update', 'HanwhaDvr')"
                class="border-0"
                :to="`/hanwha-dvr/${data.item.id}/config`"
              >
                <feather-icon icon="SettingsIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("dmp.config") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('update', 'HanwhaDvr')"
                class="border-0"
                button
                @click="confirmDelete(data.item.id)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.delete") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('create', 'HanwhaCamera') && data.item.http_schema != 'tcp'"
                class="border-0"
                button
                @click="openSyncCameras(data.item)"
              >
                <feather-icon icon="RefreshCwIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.sync") + " " + $tc("hanwha_camera.title", 2) }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('read', 'HanwhaCamera')"
                class="border-0"
                button
                @click="goToCameras(data.item)"
              >
                <feather-icon icon="VideoIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $tc("hanwha_camera.title", 2) }}
                </span>
              </b-list-group-item>

              <!-- <b-list-group-item
                v-if="$can('create', 'HanwhaCamera')"
                class="border-0"
                button
                @click="openUpdatePasswordAdmin(data.item)"
              >
                <feather-icon icon="UserIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $tc("hanwha_dvr.updateAdminUserPassword") }}
                </span>
              </b-list-group-item> -->

            </b-list-group>
          </b-popover>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted text-capitalize">{{
              $t("table.showing", {
                from: dataMeta.from,
                to: dataMeta.to,
                of: dataMeta.of,
              })
            }}</span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <Pagination
              :totalList="totalList"
              :currentPage="currentPage"
              @updatePagination="update"
              :perPage="perPage"
            >
            </Pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <cameras-wizard
      v-if="isSyncCamerasActive"
      :preselected-dvr="dvrToSync"
      :center-options="centerOptions"
      :dvr-options="dvrOptions"
      :reseller-options="resellerOptions"
      :sector-options="sectorOptions"
      :unit-options="unitOptions"
      @close-wizard="closeSyncCameras"
      @refresh="refetchData"
    />

    <UpdatePasswordAdmin
      :hanwhaDvr="currentDvrSelected"
      :isModalUpdatePasswordAdminActive="isModalUpdatePasswordAdminActive"
      @close-modal="closeUpdatePasswordAdmin"
      :is-active.sync="isModalUpdatePasswordAdminActive"
    />
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BPagination,
  BInputGroup,
  BMedia,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { computed, onMounted, onUnmounted, ref, watch } from "@vue/composition-api";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import Filters from "./Filters.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import Pagination from "@/components/Pagination.vue";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import UsersInfo from './dvr-users/UsersInfo.vue';
import CamerasWizard from '../camera/CamerasWizard/index.vue';
import UpdatePasswordAdmin from "./UpdatePasswordAdmin.vue";
import FacetedSearch from "@/components/FacetedSearch.vue";

export default {
  components: {
    Sidenav,
    Filters,
    Pagination,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BInputGroup,
    BMedia,
    vSelect,
    BreadcrumbMenu,
    FilterModal,
    ButtonFilters,
    UsersInfo,
    CamerasWizard,
    UpdatePasswordAdmin,
    FacetedSearch
  },

  setup(_, context) {
    const isSidebarActiveByChatbot = store.getters["chatbot/getIsSidebarActive"];

    //start pagination
    const update = (data) => {
      currentPage.value = data;
    };
    //end pagination

    const isSidebarActive = ref(isSidebarActiveByChatbot);
    const itemEdit = ref({});
    const isAdd = ref(false);
    const isModalUpdatePasswordAdminActive = ref(false);
    const currentDvrSelected = ref(null);
    const isSyncCamerasActive = ref(false);
    const dvrToSync = ref(null);
    const buttonExport = ref(false);

    const exportExcel = async () => {
      action.value = "export";
      buttonExport.value = true;
      try {
        await fetchList();
      } finally {
        buttonExport.value = false;
      }
    };

    watch(
      () => {
        const query = context.root.$route && context.root.$route.query;
        if (!query) return null;
        return query.wizardData ?? null;
      },
      async (wizardData) => {
        if (!wizardData) return;

        try {
          const data = JSON.parse(decodeURIComponent(wizardData));
          const isEditItem = data.isEdit || false;
          itemEdit.value = isEditItem
            ? { ...data, from_url: true }
            : { id_unit: Number(data.id), from_url: true };
          setTimeout(() => {
            if(isEditItem) {
              itemEdit.value = { ...itemEdit.value, ...data };
              isAdd.value = false;
            } else {
              isAdd.value = true;
            }
            isSidebarActive.value = true;
          }, 200);
        } catch (error) {
          toast({
            component: ToastificationContent,
            props: {
              title:
                error.response?.data?.message ||
                t("messages.error.server_error"),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        } finally {
          const query = { ...(context.root.$route.query || {}) };
          delete query.wizardData;

          context.root.$router.replace({ query }).catch(() => {});
        }
      },
      { immediate: true }
    );

    const openSyncCameras = (dvr) => {
      dvrToSync.value = dvr.id
      isSyncCamerasActive.value = true
    };

    const closeSyncCameras = () => {
      dvrToSync.value = null
      isSyncCamerasActive.value = false
    };

    const goToCameras = (dvr) => {
      context.root.$router.push({
        name: 'hanwha_camera',
        query: { dvr: String(dvr.id) },
      });
    };

    const openUpdatePasswordAdmin = (dvr) => {
      currentDvrSelected.value = dvr;
      isModalUpdatePasswordAdminActive.value = true
    };

    const closeUpdatePasswordAdmin = () => {
      currentDvrSelected.value = null;
      isModalUpdatePasswordAdminActive.value = false;
    };

    const openEdit = (item) => {
      itemEdit.value = JSON.parse(JSON.stringify(item));
      isAdd.value = false;
      isSidebarActive.value = true;
    };
    const { t } = useI18nUtils();

    const toast = useToast();

    const trans = {
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
    };
    const searchOptions = [
      { label: t("hanwha_dvr.name"), value: "name", isArray: false },
      { label: t("unit.description"), value: "description", isArray: false },
      { label: t("unit.rems"), value: "rems", isArray: false },
      { label: t("unit.sirh"), value: "sirh", isArray: false },
      { label: t("hanwha_dvr.ip"), value: "ip", isArray: false },
      { label: t("router.ip_vpn"), value: "router_ip", isArray: false },
      { label: t("alarm_rules.user_id"), value: "user", isArray: false },
      { label: t("hanwha_dvr.recording_days"), value: "recording_days", isArray: false },
      { label: t("hanwha_dvr.days_recording_audit"), value: "days_recording_audit", isArray: false },
      { label: t("hanwha_dvr.model"), value: "model", isArray: false },
      { label: t("hanwha_dvr.firmware_version"), value: "firmware_version", isArray: false },
      { label: t("hanwha_dvr.device_type"), value: "device_type", isArray: false },
      { label: t("hanwha_dvr.local_ip"), value: "local_ip", isArray: false },
      { label: t("hanwha_dvr.mac_address"), value: "mac_address", isArray: false },
    ];

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      tenantOptions,
      tenantFilter,
      unitOptions,
      unitFilter,
      alarmProfileFilter,
      alarmProfileOptions,
      centerOptions,
      sectorOptions,
      resellerOptions,
      dvrOptions,
      clearSearchFilters,
      filtersCount,
      buildFilters,
      action,
      setFacetedFilters,
      customQueryParams,
      municipalityOptions,
      regionOptions,
    } = useList();

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: trans["messages.delete.title"],
        text: trans["messages.delete.body"],
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: trans["actions.delete"],
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("hanwha_dvr/delete", id)
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: response.message,
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 10000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 10000,
                }
              );
            });
        }
      });
    };

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
      itemEdit.value = {}
    };

    const onCopy = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: "Copiado correctamente",
          icon: "ClipboardIcon",
          variant: "success",
        },
      });
    };

    const onError = () => {
      toast({
        component: ToastificationContent,
        props: {
          title: "No se pudo copiar",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    };

    return {
      isSidebarActive,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      confirmDelete,
      update,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      tenantOptions,
      tenantFilter,
      unitOptions,
      unitFilter,
      alarmProfileFilter,
      alarmProfileOptions,
      centerOptions,
      sectorOptions,
      resellerOptions,
      dvrOptions,
      currentDvrSelected,
      isSyncCamerasActive,
      isModalUpdatePasswordAdminActive,
      dvrToSync,
      onCopy,
      onError,

      filtersCount,
      buildFilters,
      clearSearchFilters,
      setFacetedFilters,
      openSyncCameras,
      closeSyncCameras,
      goToCameras,
      openUpdatePasswordAdmin,
      closeUpdatePasswordAdmin,
      closeSyncCameras,
      exportExcel,
      buttonExport,
      searchOptions,
      customQueryParams,
      municipalityOptions,
      regionOptions,
    };
  },
};
</script>

<style scoped>
.device-list-actions {
  flex-wrap: wrap;
  gap: 0.5rem;
}

.device-list-search {
  flex: 1 1 320px;
  width: auto;
  min-width: 260px;
}

.device-list-actions .device-list-search,
.device-list-actions .btn {
  margin-right: 0 !important;
  margin-left: 0 !important;
}
</style>
